import { parseConfig } from '@utilities/theme';

/**
 * PaginationControls - Reusable pagination component for client-side rendering
 *
 * This component provides a complete pagination interface that works seamlessly with both
 * Shopify and Nosto data sources. It handles all pagination logic, URL generation, and
 * user interactions while maintaining accessibility and responsive design principles.
 *
 * Key Features:
 * - Dynamic pagination rendering with ellipsis logic for large page counts
 * - Event-driven communication with ProductGrid via identifier-based scoping
 * - Intelligent URL construction with search parameter preservation
 * - Loading state management during data transitions
 * - Responsive design with mobile-optimized controls
 *
 * Architecture:
 * - Self-contained component with template-based rendering
 * - Communicates via custom events with identifier-based scoping (not DOM-based)
 * - Supports both attribute-based and method-based data updates
 * - Maintains proper browser history with generated URLs
 *
 * Data Flow:
 * 1. Receives pagination data from ProductGrid component
 * 2. Renders pagination controls with proper page links
 * 3. Handles user clicks and emits pagination:change events with identifier
 * 4. ProductGrid matches events by identifier and updates URL/loads new data
 * 5. Component re-renders with updated pagination state
 *
 * @class
 * @extends HTMLElement
 * @customElement
 */
class PaginationControls extends HTMLElement {
  /**
   * Creates an instance of PaginationControls
   * @constructor
   */
  constructor() {
    super();
    this.elements = {};
    this.config = {};
    this.paginationData = null;
    this.baseUrl = '';
    this.isMainPagination = false;
    this.isInitialized = false;
  }

  /**
   * Lifecycle callback when the element is added to the DOM
   * @returns {void}
   */
  connectedCallback() {
    this.init();
  }

  allowDebug() {
    return this.classList.contains("debug");
  }

  logDebug(message, data = null) {
    if (!this.allowDebug()) {
      return;
    }

    if (data !== null) {
      console.log(`PaginationControls: ${message}`, data);
    } else {
      console.log(`PaginationControls: ${message}`);
    }
  }

  /**
   * Initialize the component
   * @returns {void}
   * @private
   */
  init() {
    this.logDebug('init: start');

    this.elements.container = this;
    this.elements.loadingState = this.querySelector('.pagination-loading');

    // Parse config from JSON script tag (identifier-based scoping, not DOM-based)
    this.config = parseConfig(this, this.allowDebug());
    this.logDebug('init: parsed config', { config: this.config });

    // STRICT: Require identifier for proper event scoping
    if (!this.config.identifier) {
      console.error("PaginationControls: Missing required identifier (config.identifier). PaginationControls must have an identifier for proper event scoping.", {
        element: this,
        config: this.config,
        note: "Identifier should match ProductGrid's gridId"
      });
    }

    // Get base URL from config (preferred) or dataset (fallback)
    this.baseUrl = this.config.baseUrl || this.dataset.baseUrl || this.getCurrentPageUrl();
    this.logDebug('init: base URL resolved', { baseUrl: this.baseUrl });

    // Get main pagination flag from config (preferred) or dataset (fallback)
    this.isMainPagination = this.config.isMainPagination !== undefined
      ? this.config.isMainPagination
      : this.dataset.mainPagination;
    this.logDebug('init: main pagination flag resolved', { isMainPagination: this.isMainPagination });

    // Check if initial pagination data is provided via dataset
    const initialPaginationData = this.dataset.pagination;
    if (initialPaginationData) {
      try {
        this.paginationData = JSON.parse(initialPaginationData);
        this.logDebug('init: parsed initial pagination data', { paginationData: this.paginationData });
      } catch (error) {
        console.error('PaginationControls: Invalid initial pagination data', error);
      }
    }

    this.initTemplates();
    this.initEventListeners();
    this.isInitialized = true;

    // Render if we have initial data, otherwise wait for product-grid:pagination-updated event
    if (this.paginationData && this.paginationData.pages > 1) {
      this.logDebug('init: rendering with initial pagination data');
      this.render();
    } else {
      this.logDebug('init: showing loading state while waiting for pagination data');
      this.showLoadingState();
      // Late-subscriber handshake: if this control upgraded after the grid
      // already broadcast its one-shot pagination event (the early-search primer
      // can make the grid render before this module loads), ask the grid to
      // replay it. Harmless if the grid hasn't rendered yet — the listener above
      // still catches the normal dispatch.
      this.requestGridState();
    }

    this.classList.add("pagination-controls--initialized");
    this.logDebug('init: completed', { isInitialized: this.isInitialized });
  }

  /**
   * Initialize event listeners
   * Listens for pagination updates from ProductGrid via events
   * @returns {void}
   * @private
   */
  initEventListeners() {
    // Bind handler once and store reference for cleanup
    this.boundHandlePaginationUpdate = this.handlePaginationUpdate.bind(this);

    // Listen for pagination updates from ProductGrid (event-based, not direct method calls)
    document.addEventListener(
      'product-grid:pagination-updated',
      this.boundHandlePaginationUpdate
    );
  }

  /**
   * Ask the matching ProductGrid to replay its latest pagination broadcast.
   * Used when this control connects after the grid already dispatched, closing
   * the init/render race. The grid responds via its product-grid:state-request
   * handler with the cached product-grid:pagination-updated event.
   * @returns {void}
   * @private
   */
  requestGridState() {
    document.dispatchEvent(
      new CustomEvent('product-grid:state-request', {
        detail: { identifier: this.config.identifier },
        bubbles: true,
      })
    );
  }

  /**
   * Lifecycle callback when the element is removed from the DOM
   * Cleans up event listeners
   * @returns {void}
   */
  disconnectedCallback() {
    // Clean up event listeners
    if (this.boundHandlePaginationUpdate) {
      document.removeEventListener(
        'product-grid:pagination-updated',
        this.boundHandlePaginationUpdate
      );
      this.boundHandlePaginationUpdate = null;
    }
  }

  /**
   * Check if a ProductGrid pagination update event is intended for this PaginationControl
   * @param {CustomEvent} event - product-grid:pagination-updated event
   * @returns {boolean}
   * @private
   */
  shouldRespondToPaginationUpdate(event) {
    const eventIdentifier = event.detail?.identifier;

    // STRICT: Require identifier in event
    if (!eventIdentifier) {
      if (this.allowDebug()) {
        console.warn("PaginationControls: ProductGrid pagination update event missing identifier - ignoring", event);
      }
      return false;
    }

    // STRICT: Require this control to have an identifier
    if (!this.config.identifier) {
      if (this.allowDebug()) {
        console.warn("PaginationControls: Missing identifier (config.identifier) - cannot match events", {
          eventIdentifier,
          config: this.config
        });
      }
      return false;
    }

    const controlIdentifier = String(this.config.identifier).toLowerCase();
    const identifierLower = String(eventIdentifier).toLowerCase();

    // Match if identifier exactly equals control identifier or ends with it (with or without separator)
    // This handles both simple identifiers (section ID) and compound identifiers (collection-section-block)
    const matches = identifierLower === controlIdentifier ||
                   identifierLower.endsWith(`-${controlIdentifier}`) ||
                   identifierLower.endsWith(controlIdentifier);

    if (!matches && this.allowDebug()) {
      this.logDebug('PaginationControls: ProductGrid pagination update identifier mismatch - ignoring', {
        eventIdentifier,
        controlIdentifier,
        matches
      });
    }

    return matches;
  }

  /**
   * Handle pagination update events from ProductGrid
   * @param {CustomEvent} event - product-grid:pagination-updated event
   * @private
   */
  handlePaginationUpdate(event) {
    // Check if this event is intended for this PaginationControl
    if (!this.shouldRespondToPaginationUpdate(event)) {
      return;
    }

    const { paginate, baseUrl } = event.detail;

    this.logDebug('handlePaginationUpdate: received event', { paginate, baseUrl });

    // Update pagination data
    this.updatePagination(paginate, baseUrl);
  }

  /**
   * Initialize template references
   * @returns {void}
   * @private
   */
  initTemplates() {
    this.elements.templates = {
      pagination: this.querySelector('#pagination-template'),
      item: this.querySelector('#pagination-item-template'),
      current: this.querySelector('#pagination-current-template'),
      ellipsis: this.querySelector('#pagination-ellipsis-template'),
      prev: this.querySelector('#pagination-prev-template'),
      next: this.querySelector('#pagination-next-template')
    };
  }

  /**
   * Public method to update pagination data (called by ProductGrid component)
   * Renders new pagination controls or hides component if only one page
   * @param {Object} paginationData - Pagination data from Shopify or Nosto response
   * @param {string} baseUrl - Base URL for pagination links (required)
   * @returns {void}
   */
  updatePagination(paginationData, baseUrl) {
    this.logDebug('updatePagination: called', { paginationData, baseUrl });

    this.paginationData = paginationData;
    this.baseUrl = baseUrl;
    this.logDebug('updatePagination: base URL set', { baseUrl: this.baseUrl });

    if (this.paginationData && this.paginationData.pages > 1) {
      this.logDebug('updatePagination: rendering pagination', {
        currentPage: this.getCurrentPageFromData(),
        totalPages: this.paginationData.pages
      });
      this.render();
    } else {
      this.logDebug('updatePagination: hiding component due to insufficient pages');
      this.hide();
    }
  }

  /**
   * Get current page from pagination data, supporting both camelCase and snake_case
   * @returns {number}
   * @private
   */
  getCurrentPageFromData() {
    if (!this.paginationData) return 1;
    return this.paginationData.current_page ?? this.paginationData.currentPage ?? 1;
  }

  /**
   * Show loading state
   * @returns {void}
   * @private
   */
  showLoadingState() {
    if (this.elements.loadingState) {
      this.elements.loadingState.style.display = '';
      this.elements.loadingState.setAttribute('aria-hidden', 'false');
      this.logDebug('showLoadingState: display set to visible');
    }
  }

  /**
   * Hide loading state
   * @returns {void}
   * @private
   */
  hideLoadingState() {
    if (this.elements.loadingState) {
      this.elements.loadingState.style.display = 'none';
      this.elements.loadingState.setAttribute('aria-hidden', 'true');
      this.logDebug('hideLoadingState: display set to none');
    }
  }

  /**
   * Hide the entire pagination component
   * @returns {void}
   * @private
   */
  hide() {
    this.style.display = 'none';
    this.logDebug('hide: component hidden');
  }

  /**
   * Show the pagination component
   * @returns {void}
   * @private
   */
  show() {
    this.style.display = '';
    this.logDebug('show: component shown');
  }

  /**
   * Render pagination
   * @returns {void}
   * @private
   */
  render() {
    if (!this.paginationData || !this.elements.templates.pagination) {
      this.logDebug('render: aborted due to missing pagination data or template');
      return;
    }

    this.logDebug('render: start', {
      currentPage: this.getCurrentPageFromData(),
      totalPages: this.paginationData.pages
    });

    this.hideLoadingState();
    this.show();

    // Clear existing pagination content (except templates and loading state)
    const existingPagination = this.querySelector('.pagination');
    if (existingPagination) {
      existingPagination.remove();
      this.logDebug('render: removed existing pagination element');
    }

    // Clone pagination template
    const paginationTemplate = this.elements.templates.pagination.content.cloneNode(true);
    const paginationList = paginationTemplate.querySelector('.pagination__list');

    if (!paginationList) {
      this.logDebug('render: pagination list not found in template');
      this.appendChild(paginationTemplate);
      return;
    }

    const currentPage = this.getCurrentPageFromData();
    const totalPages = this.paginationData.pages;

    // Generate pagination items
    const paginationItems = this.generatePaginationItems(currentPage, totalPages);
    this.logDebug('render: generated pagination items', paginationItems);

    // Add previous button
    if (currentPage > 1) {
      const prevItem = this.createPaginationItem('prev', currentPage - 1);
      if (prevItem) {
        paginationList.appendChild(prevItem);
        this.logDebug('render: appended previous button', { targetPage: currentPage - 1 });
      }
    }

    // Add pagination items
    paginationItems.forEach(item => {
      const paginationItem = this.createPaginationItem(item.type, item.page, item.text);
      if (paginationItem) {
        paginationList.appendChild(paginationItem);
        this.logDebug('render: appended pagination item', item);
      }
    });

    // Add next button
    if (currentPage < totalPages) {
      const nextItem = this.createPaginationItem('next', currentPage + 1);
      if (nextItem) {
        paginationList.appendChild(nextItem);
        this.logDebug('render: appended next button', { targetPage: currentPage + 1 });
      }
    }

    // Append to container
    this.appendChild(paginationTemplate);
    this.logDebug('render: appended pagination template to container');

    // Set up event listeners
    this.setupEventListeners();
    this.logDebug('render: event listeners setup complete');
  }

  /**
   * Generate pagination items with intelligent ellipsis logic for large page sets
   * Implements smart truncation showing first, last, current, and neighboring pages
   * @param {number} currentPage - Current active page number
   * @param {number} totalPages - Total number of pages available
   * @returns {Array} Array of pagination item objects with type, page, and text properties
   * @private
   */
  generatePaginationItems(currentPage, totalPages) {
    this.logDebug('generatePaginationItems: start', { currentPage, totalPages });

    const items = [];
    const delta = 2; // Number of pages to show around current page

    if (totalPages <= 7) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push({
          type: i === currentPage ? 'current' : 'item',
          page: i,
          text: i.toString()
        });
      }
    } else {
      // Always show first page
      items.push({
        type: currentPage === 1 ? 'current' : 'item',
        page: 1,
        text: '1'
      });

      // Calculate range around current page
      const rangeStart = Math.max(2, currentPage - delta);
      const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

      // Add ellipsis after first page if needed
      if (rangeStart > 2) {
        items.push({ type: 'ellipsis' });
      }

      // Add pages around current page
      for (let i = rangeStart; i <= rangeEnd; i++) {
        items.push({
          type: i === currentPage ? 'current' : 'item',
          page: i,
          text: i.toString()
        });
      }

      // Add ellipsis before last page if needed
      if (rangeEnd < totalPages - 1) {
        items.push({ type: 'ellipsis' });
      }

      // Always show last page (if different from first)
      if (totalPages > 1) {
        items.push({
          type: currentPage === totalPages ? 'current' : 'item',
          page: totalPages,
          text: totalPages.toString()
        });
      }
    }

    this.logDebug('generatePaginationItems: result', items);
    return items;
  }

  /**
   * Create pagination item from template
   * @param {string} type - Type of pagination item
   * @param {number} page - Page number
   * @param {string} text - Display text
   * @returns {HTMLElement|null}
   * @private
   */
  createPaginationItem(type, page, text = null) {
    const template = this.elements.templates[type];
    if (!template) return null;

    const clone = template.content.cloneNode(true);
    const link = clone.querySelector('[data-pagination-link]');
    const textElement = clone.querySelector('[data-pagination-text]');

    if (link && page) {
      const url = this.buildPageUrl(page);
      link.href = url;
      link.setAttribute('data-page', page);

      // Replace [[page]] placeholder in the id attribute with actual page number
      if (link.id && link.id.includes('[[page]]')) {
        link.id = link.id.replace('[[page]]', page);
      }

      this.logDebug('createPaginationItem: configured link', { type, page, url });
    }

    if (textElement && (text || page)) {
      textElement.textContent = text || page.toString();
      this.logDebug('createPaginationItem: set text', { type, text: text || page.toString() });
    }

    return clone;
  }

  /**
   * Build URL for specific page
   * Only includes page parameter in URL if this is the main pagination
   * Non-main pagination instances don't modify URLs
   * @param {number} page
   * @returns {string}
   * @private
   */
  buildPageUrl(page) {
    try {
      const url = new URL(this.baseUrl, window.location.origin);
      const params = new URLSearchParams(window.location.search);

      // Only modify page parameter if this is the main pagination
      if (this.isMainPagination) {
        if (page > 1) {
          params.set('page', page);
        } else {
          params.delete('page');
        }
      }

      url.search = params.toString();
      this.logDebug('buildPageUrl: constructed URL', {
        page,
        isMainPagination: this.isMainPagination,
        url: url.toString()
      });
      return url.toString();
    } catch (error) {
      this.logDebug('buildPageUrl: error encountered', { page, error });
      console.error('PaginationControls: Error building page URL', error);
      return '#';
    }
  }

  /**
   * Get current page URL without query parameters
   * @returns {string}
   * @private
   */
  getCurrentPageUrl() {
    return window.location.pathname;
  }

  /**
   * Set up event listeners for pagination links
   * @returns {void}
   * @private
   */
  setupEventListeners() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-pagination-link]');

      if (link) {
        event.preventDefault();

        const page = parseInt(link.getAttribute('data-page'), 10);
        if (page && page !== this.getCurrentPageFromData()) {
          this.logDebug('setupEventListeners: pagination link clicked', { page });
          this.handlePageChange(page);
        }
      }
    });
  }


  /**
   * Handle page change event and notify parent components
   * Emits pagination:change event for ProductGrid to handle data loading and URL updates
   * @param {number} page - Target page number to navigate to
   * @returns {void}
   * @private
   */
  handlePageChange(page) {
    this.logDebug('handlePageChange: dispatching event', { page, identifier: this.config.identifier });

    // STRICT: Require identifier for proper event scoping
    if (!this.config.identifier) {
      console.error("PaginationControls: Cannot dispatch pagination:change event - missing identifier", {
        element: this,
        page,
        config: this.config,
        note: "Identifier should be set in JSON config and match ProductGrid's gridId"
      });
      return;
    }

    // Emit custom event for ProductGrid to handle (identifier-based scoping, not DOM-based)
    const event = new CustomEvent('pagination:change', {
      detail: {
        page,
        identifier: this.config.identifier
      },
      bubbles: true
    });

    this.dispatchEvent(event);
    this.logDebug('handlePageChange: event dispatched', { identifier: this.config.identifier });
  }

  /**
   * Get current page number
   * @returns {number}
   */
  getCurrentPage() {
    return this.getCurrentPageFromData();
  }

  /**
   * Get total pages
   * @returns {number}
   */
  getTotalPages() {
    return this.paginationData?.pages || 0;
  }

  /**
   * Check if pagination has multiple pages
   * @returns {boolean}
   */
  hasMultiplePages() {
    return this.getTotalPages() > 1;
  }
}

customElements.define('pagination-controls', PaginationControls);
